import { getOrCreateFrameId } from '../../utils';
import {
  AudienceFilterContext,
  IAudienceFilter,
  IAudienceFilterPredicate,
} from './interfaces';

export class TargetAppPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { apps }: IAudienceFilter,
    { client: { appName } }: AudienceFilterContext
  ): boolean {
    return apps.size === 0 || apps.has(appName);
  }
}

export class MatchingTagPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { tags }: IAudienceFilter,
    { client: { tags: clientTags } }: AudienceFilterContext
  ): boolean {
    return (
      tags.size === 0 ||
      Array.from(tags).some((tag) => clientTags.includes(tag))
    );
  }
}

export class ExcludeSelfPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { excludeSelf }: IAudienceFilter,
    { subscriberClientId, message: { sender } }: AudienceFilterContext
  ): boolean {
    return !excludeSelf || subscriberClientId !== sender;
  }
}

export class SameWindowPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { sameWindow }: IAudienceFilter,
    { message: { senderFrameId } }: AudienceFilterContext
  ): boolean {
    const currentFrameId = getOrCreateFrameId();
    return !sameWindow || senderFrameId === currentFrameId;
  }
}

export class CrossTabPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { allowCrossTab }: IAudienceFilter,
    { message: { senderBrokerId }, subscriberBrokerId }: AudienceFilterContext
  ): boolean {
    return allowCrossTab || senderBrokerId === subscriberBrokerId;
  }
}

export class CrossPortalPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { allowCrossPortal }: IAudienceFilter,
    { message: { senderPortalId }, client: { portalId } }: AudienceFilterContext
  ): boolean {
    const areClientAndSenderHubless = !senderPortalId && !portalId;

    return (
      allowCrossPortal ||
      areClientAndSenderHubless ||
      senderPortalId === portalId
    );
  }
}

/**
 * Predicate that checks if a client matches the label requirements of an audience filter.
 * For each label in the audience filter we MUST have a matching value for that label in the client's context.
 */
export class MatchingLabelPredicate implements IAudienceFilterPredicate {
  isTargetAudience(
    { labels }: IAudienceFilter,
    { client }: AudienceFilterContext
  ): boolean {
    return Array.from(labels.entries()).every(([key, allowedValues]) => {
      const clientValues = new Set(client.labels?.[key] || []);
      return Array.from(allowedValues).some((value) => clientValues.has(value));
    });
  }
}
